import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Circle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import Footer from './Footer';

// Export tasks so they can be used by the news ticker in TopNav
export const buildingInPublicTasks = [
  { id: 1, task: "Refactoring midpantry.com landing page copy", time: "09:30", status: "complete" },
  { id: 2, task: "Reviewing Q1 2026 OKRs for FineLine Retail", time: "10:15", status: "complete" },
  { id: 3, task: "Writing new Reliquaries post on distributed systems", time: "11:00", status: "in-progress" },
  { id: 4, task: "Debugging authentication flow for client project", time: "12:30", status: "pending" },
  { id: 5, task: "Recording podcast episode on entrepreneurship", time: "14:00", status: "pending" },
  { id: 6, task: "Reviewing pull requests for open source contributions", time: "15:30", status: "pending" },
  { id: 7, task: "Preparing pitch deck for investor meeting", time: "16:00", status: "pending" },
  { id: 8, task: "Evening meditation and journaling", time: "20:00", status: "pending" },
];

const BuildingInPublicPage = () => {
  const { isDark } = useTheme();

  useEffect(() => {
    // Set page title and meta tags
    document.title = 'Shteynberg | Building in Public';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Don't be a stranger and keep up-to-date on my personal and professional development.";

    // Add/update og:title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = 'Alan Shteynberg | Building in Public';

    return () => {
      document.title = 'Alan was here.';
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return '#22c55e';
      case 'in-progress': return '#f46c38';
      case 'pending': return isDark ? '#64748b' : '#94a3b8';
      default: return '#64748b';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'complete': return 'Complete';
      case 'in-progress': return 'In Progress';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  return (
    <div className="building-page" data-testid="building-in-public-page">
      {/* Back Button */}
      <Link to="/" className="back-button" data-testid="building-back-button">
        <ArrowLeft size={18} />
        Back to Home
      </Link>

      {/* Header */}
      <header className="page-header">
        <div className="live-indicator">
          <span className="live-dot" />
          <span className="live-text">LIVE</span>
        </div>
        <h1 className="page-title">Building in Public</h1>
        <p className="page-subtitle">
          Don't be a stranger and keep up-to-date on my personal and professional development.
        </p>
      </header>

      {/* Today's Tasks */}
      <section className="tasks-section">
        <h2 className="section-title">Today's Manifest</h2>
        <div className="tasks-list">
          {buildingInPublicTasks.map((item) => (
            <div key={item.id} className={`task-item ${item.status}`} data-testid={`task-${item.id}`}>
              <div className="task-time">{item.time}</div>
              <div className="task-content">
                <span className="task-text">{item.task}</span>
              </div>
              <div className="task-status">
                <Circle 
                  size={8} 
                  fill={getStatusColor(item.status)} 
                  color={getStatusColor(item.status)} 
                />
                <span style={{ color: getStatusColor(item.status) }}>
                  {getStatusLabel(item.status)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Footer note */}
      <p className="footer-note">
        This page updates throughout the day as tasks are completed.
      </p>
      
      <Footer />

      <style jsx>{`
        .building-page {
          min-height: 100vh;
          background: ${isDark ? '#151312' : '#f8fafc'};
          padding: 40px;
          max-width: 900px;
          margin: 0 auto;
          transition: background-color 0.3s ease;
        }

        .back-button {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#998f8f' : '#64748b'};
          text-decoration: none;
          padding: 10px 16px;
          border-radius: 8px;
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
          transition: all 0.2s ease;
          margin-bottom: 40px;
        }

        .back-button:hover {
          color: ${isDark ? '#fff' : '#151312'};
          border-color: ${isDark ? 'rgba(182, 180, 189, 0.4)' : 'rgba(0, 0, 0, 0.2)'};
        }

        .page-header {
          margin-bottom: 48px;
        }

        .live-indicator {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding: 6px 12px;
          background: ${isDark ? 'rgba(244, 108, 56, 0.1)' : 'rgba(244, 108, 56, 0.1)'};
          border-radius: 20px;
        }

        .live-dot {
          width: 8px;
          height: 8px;
          background: #f46c38;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }

        .live-text {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          color: #f46c38;
          letter-spacing: 0.1em;
        }

        .page-title {
          font-family: 'Poppins', sans-serif;
          font-size: clamp(36px, 6vw, 56px);
          font-weight: 700;
          color: ${isDark ? '#fff' : '#151312'};
          line-height: 1.1;
          margin: 0 0 16px 0;
          transition: color 0.3s ease;
        }

        .page-subtitle {
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          color: ${isDark ? '#998f8f' : '#64748b'};
          line-height: 1.6;
          margin: 0;
          max-width: 500px;
          transition: color 0.3s ease;
        }

        .tasks-section {
          margin-bottom: 48px;
        }

        .section-title {
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 600;
          color: ${isDark ? '#998f8f' : '#64748b'};
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin: 0 0 20px 0;
          transition: color 0.3s ease;
        }

        .tasks-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
          background: ${isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)'};
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.1)' : 'rgba(0, 0, 0, 0.06)'};
          border-radius: 12px;
          overflow: hidden;
        }

        .task-item {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 16px 20px;
          background: ${isDark ? 'rgba(21, 19, 18, 0.5)' : 'rgba(255, 255, 255, 0.8)'};
          transition: background-color 0.2s ease;
        }

        .task-item:hover {
          background: ${isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
        }

        .task-item.complete {
          opacity: 0.6;
        }

        .task-item.complete .task-text {
          text-decoration: line-through;
        }

        .task-time {
          font-family: 'Inter', monospace;
          font-size: 13px;
          font-weight: 500;
          color: ${isDark ? '#998f8f' : '#64748b'};
          min-width: 50px;
          transition: color 0.3s ease;
        }

        .task-content {
          flex: 1;
        }

        .task-text {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#fff' : '#151312'};
          transition: color 0.3s ease;
        }

        .task-status {
          display: flex;
          align-items: center;
          gap: 6px;
          font-family: 'Inter', sans-serif;
          font-size: 12px;
          font-weight: 500;
          min-width: 100px;
          justify-content: flex-end;
        }

        .footer-note {
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
          color: ${isDark ? 'rgba(153, 143, 143, 0.6)' : 'rgba(100, 116, 139, 0.6)'};
          text-align: center;
          margin: 0;
          transition: color 0.3s ease;
        }

        @media (max-width: 600px) {
          .building-page {
            padding: 24px 16px;
          }

          .task-item {
            flex-wrap: wrap;
            gap: 8px;
          }

          .task-time {
            min-width: auto;
          }

          .task-content {
            flex-basis: 100%;
            order: 3;
          }

          .task-status {
            min-width: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default BuildingInPublicPage;
